import { useEffect } from 'react';
import { Alert } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { convertWinToIANA } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../Common';
import { Textarea, toasty } from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';

const formatInJobTz = (utcIso, tz) => {
    if (!utcIso) return '';
    const iana = tz ? convertWinToIANA(tz) || tz : null;
    const m = iana ? moment.utc(utcIso).tz(iana) : moment.utc(utcIso).local();
    return m.format('MMM D, YYYY h:mm A z');
};

const DismissedHistory = ({ flags }) => (
    <div className="mt-3">
        <h6 className="text-muted">Dismissed History</h6>
        <ul className="list-unstyled small">
            {flags.map((f) => (
                <li
                    key={f.id}
                    className="border-bottom py-1"
                >
                    <div>
                        <strong>{f.flagType} – {f.category}</strong>
                        {f.employeeName && <span className="text-muted ml-1">({f.employeeName})</span>}
                    </div>
                    <div className="text-muted">
                        Dismissed by {f.dismissedByName || 'unknown'}
                        {f.dismissedAt && ` on ${moment.utc(f.dismissedAt).local().format('MMM D, YYYY h:mm A')}`}
                    </div>
                    {f.dismissalNotes && <div className="font-italic">{f.dismissalNotes}</div>}
                </li>
            ))}
        </ul>
    </div>
);

const FlagDismissalModalBody = ({
    timesheetId, initialFlagId, setFieldValue, values,
}) => {
    const flagsQuery = useQuery({
        queryKey: ['timesheetFlags', timesheetId],
        queryFn: () => httpClient.js(ApiRoutes.timesheetManagement.flags(timesheetId)),
        enabled: !!timesheetId,
        select: (data) => {
            const all = Array.isArray(data) ? data : [];
            const active = all.filter((f) => !f.isDismissed);
            return {
                active,
                dismissed: all.filter((f) => f.isDismissed),
                activeOptions: active.map((f) => ({
                    value: Number(f.id),
                    label: `${f.flagType} – ${f.category}${f.employeeName ? ` (${f.employeeName})` : ''}`,
                })),
            };
        },
    });

    const { active = [], dismissed = [], activeOptions = [] } = flagsQuery.data || {};

    useEffect(() => {
        if (flagsQuery.isError) toasty.error('Failed to load flags.');
    }, [flagsQuery.isError]);

    // Once flags load, reset stale form state and preselect the requested (or first) active flag.
    useEffect(() => {
        if (!flagsQuery.isSuccess) return;
        const preselect = initialFlagId
            && active.some((f) => Number(f.id) === Number(initialFlagId))
            ? Number(initialFlagId)
            : active[0]?.id;
        setFieldValue('flagId', preselect != null ? Number(preselect) : null);
        setFieldValue('notes', '');
    }, [flagsQuery.isSuccess, initialFlagId, active, setFieldValue]);

    const selectedId = values?.flagId != null ? Number(values.flagId) : null;
    const selected = active.find((f) => Number(f.id) === selectedId);

    if (flagsQuery.isLoading) {
        return <div className="text-muted">Loading flags…</div>;
    }

    if (flagsQuery.isError) {
        return <div className="text-danger">Failed to load flags.</div>;
    }

    if (active.length === 0) {
        return (
            <>
                <Alert
                    color="info"
                    fade={false}
                >No active flags on this timesheet.</Alert>
                {dismissed.length > 0 && <DismissedHistory flags={dismissed} />}
            </>
        );
    }

    return (
        <>
            <ValidatedSelect
                label="Flag"
                id="flagId"
                name="flagId"
                required
                asPrimitive
                hideClear
                options={activeOptions}
            />

            {selected && (
                <div className="border rounded p-2 mb-3 bg-light">
                    <div>
                        <strong>Type:</strong>
                        {' '}
                        {selected.flagType}
                    </div>
                    <div>
                        <strong>Category:</strong>
                        {' '}
                        {selected.category}
                    </div>
                    {selected.employeeName && (
                        <div>
                            <strong>Employee:</strong>
                            {' '}
                            {selected.employeeName}
                        </div>
                    )}
                    {selected.equipmentName && (
                        <div>
                            <strong>Equipment:</strong>
                            {' '}
                            {selected.equipmentName}
                        </div>
                    )}
                    <div>
                        <strong>Job start:</strong>
                        {' '}
                        {formatInJobTz(selected.jobStartUtc, selected.timeZone)}
                    </div>
                    <div>
                        <strong>Alerted:</strong>
                        {' '}
                        {formatInJobTz(selected.alertedOnUtc, selected.timeZone)}
                    </div>
                    {selected.createdByName && (
                        <div>
                            <strong>Created by:</strong>
                            {' '}
                            {selected.createdByName}
                        </div>
                    )}
                </div>
            )}

            <Textarea
                label="Dismissal notes (optional)"
                id="notes"
                name="notes"
                placeholder="Why is this flag being dismissed?"
                maxLength="500"
                rows="4"
            />

            {dismissed.length > 0 && <DismissedHistory flags={dismissed} />}
        </>
    );
};

export default FlagDismissalModalBody;
